import React, { useEffect } from "react";
import {
  FileText,
  Users,
  MessageSquare,
  ArrowUp,
  AlertCircle,
  RefreshCcw,
  ArrowLeft,
} from "lucide-react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";
import CommonSidebar from "../../components/dashboard/CommonSidebar";
import { useDashboard } from "../../hooks/dashboard/useDashboard";

export default function AdminDashboard() {
  const { dashboardData, getDashboardData, transformedData, isLoading, error } =
    useDashboard();

  useEffect(() => {
    getDashboardData();
  }, [getDashboardData]);

  const stats = [
    {
      title: "오늘 배포된 설문",
      value: dashboardData?.data.today_deploy_survey || 0,
      icon: FileText,
      bgGradient: "from-purple-500 to-purple-600",
      iconBg: "bg-purple-100",
      iconColor: "text-purple-600",
    },
    {
      title: "전체 유저 수",
      value: dashboardData?.data.total_user || 0,
      icon: Users,
      bgGradient: "from-blue-500 to-blue-600",
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600",
    },
    {
      title: "미처리된 문의",
      value: dashboardData?.data.unsolved_odo || 0,
      icon: MessageSquare,
      bgGradient: "from-orange-500 to-orange-600",
      iconBg: "bg-orange-100",
      iconColor: "text-orange-600",
    },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 lg:ml-72">
        <div className="bg-slate-900 border-b border-slate-800 p-6">
          <h1 className="text-2xl font-bold text-white">대시보드</h1>
        </div>
        <CommonSidebar />
        <div className="p-6">
          <div className="flex justify-center items-center h-screen">
            <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-purple-500"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 lg:ml-72">
        <div className="bg-slate-900 border-b border-slate-800 p-6">
          <h1 className="text-2xl font-bold text-white">대시보드</h1>
        </div>
        <CommonSidebar />
        <div className="p-6">
          <div className="flex justify-center items-center min-h-[60vh]">
            <div className="bg-slate-900 rounded-xl border border-red-500/20 shadow-2xl max-w-md w-full mx-4">
              {/* 에러 헤더 */}
              <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 rounded-t-xl p-6 border-b border-red-500/20">
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                      <AlertCircle className="w-6 h-6 text-red-500" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white mb-1">
                      오류가 발생했습니다
                    </h3>
                    <p className="text-slate-400 text-sm">
                      데이터를 불러오는 중 문제가 발생했습니다
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <AlertCircle className="w-4 h-4 text-red-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-slate-400 mb-2">
                        오류 메시지
                      </h4>
                      <p className="text-base text-red-400 font-mono bg-red-500/5 p-2 rounded border border-red-500/20 break-all">
                        {error}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 pt-0">
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => window.location.reload()}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-slate-900"
                  >
                    <RefreshCcw className="w-4 h-4" />
                    새로고침
                  </button>
                  <button
                    onClick={() => window.history.back()}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 focus:ring-offset-slate-900"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    이전 페이지
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 lg:ml-72">
      {/* 헤더 */}
      <div className="bg-slate-900 border-b border-slate-800 p-6">
        <h1 className="text-2xl font-bold text-white">대시보드</h1>
      </div>
      <CommonSidebar />

      {/* 메인 컨텐츠 */}
      <div className="p-6">
        {/* 상단 통계 카드 */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="bg-slate-900 rounded-xl border border-slate-800 p-6 hover:border-slate-700 transition-all duration-300"
            >
              <div className="flex items-center justify-between mb-4">
                <div
                  className={`w-12 h-12 ${stat.iconBg} rounded-lg flex items-center justify-center`}
                >
                  <stat.icon className={`w-6 h-6 ${stat.iconColor}`} />
                </div>
                <div
                  className={`flex items-center gap-1 text-sm ${
                    stat.value ? "text-green-400" : "text-red-400"
                  }`}
                >
                  <ArrowUp className="w-4 h-4" />
                  <span className="text-sm">{stat.value}</span>
                </div>
              </div>
              <h3 className="text-slate-400 text-sm font-medium mb-1">
                {stat.title}
              </h3>
              <p className="text-3xl font-bold text-white">{stat.value}</p>
            </div>
          ))}
        </div>

        {/* 그래프 섹션 */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* 일주일간 가입 유저 수 */}
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-white">
                  일주일간 가입 유저 수
                </h3>
                <p className="text-sm text-slate-400 mt-1">
                  최근 7일간 신규 가입자 추이
                </p>
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={transformedData?.weeklyRegistUser}>
                  <defs>
                    <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis
                    dataKey="day"
                    stroke="#64748b"
                    style={{ fontSize: "12px" }}
                  />
                  <YAxis stroke="#64748b" style={{ fontSize: "12px" }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1e293b",
                      border: "1px solid #334155",
                      borderRadius: "8px",
                    }}
                    labelStyle={{ color: "#e2e8f0" }}
                    itemStyle={{ color: "#a78bfa" }}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#8b5cf6"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorUsers)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* 일주일간 완료한 설문 갯수 */}
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-white">
                  일주일간 완료한 설문
                </h3>
                <p className="text-sm text-slate-400 mt-1">
                  최근 7일간 완료된 설문 추이
                </p>
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={transformedData?.weeklyCompleteSurvey}>
                  <defs>
                    <linearGradient
                      id="colorSurveys"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis
                    dataKey="day"
                    stroke="#64748b"
                    style={{ fontSize: "12px" }}
                  />
                  <YAxis stroke="#64748b" style={{ fontSize: "12px" }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1e293b",
                      border: "1px solid #334155",
                      borderRadius: "8px",
                    }}
                    labelStyle={{ color: "#e2e8f0" }}
                    itemStyle={{ color: "#60a5fa" }}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorSurveys)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* 하단 추가 정보 카드 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
            <h4 className="text-white font-semibold mb-4">오늘의 활동</h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-slate-400 text-sm">신규 가입</span>
                <span className="text-white font-medium">
                  {dashboardData?.data.today_active.new_member}명
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400 text-sm">활성 사용자</span>
                <span className="text-white font-medium">
                  {dashboardData?.data.today_active.active_user}명
                </span>
              </div>
            </div>
          </div>

          <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
            <h4 className="text-white font-semibold mb-4">인기 에이전트</h4>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <span className="text-purple-400 text-xs font-bold">1</span>
                </div>
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">
                    {dashboardData?.data.popular_agents[0].agent_title}
                  </p>
                  <p className="text-slate-500 text-xs">
                    사용 횟수: {dashboardData?.data.popular_agents[0].usage}회
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <span className="text-blue-400 text-xs font-bold">2</span>
                </div>
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">
                    {dashboardData?.data.popular_agents[1].agent_title}
                  </p>
                  <p className="text-slate-500 text-xs">
                    사용 횟수: {dashboardData?.data.popular_agents[1].usage}회
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
            <h4 className="text-white font-semibold mb-4">
              오픈 설문 시스템 상태
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-slate-400 text-sm">배포중인 설문</span>
                <span className="text-green-400 font-medium">
                  {dashboardData?.data.open_survey_status.deploying_survey}개
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400 text-sm">
                  오늘 종료되는 설문
                </span>
                <span className="text-green-400 font-medium">
                  {dashboardData?.data.open_survey_status.today_closing_survey}개
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
